import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  ChatBubbleLeftRightIcon,
  ChevronRightIcon,
  ClockIcon,
  EyeIcon,
  CheckCircleIcon,
  LockClosedIcon,
  FlagIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, Avatar, InlineSpinner, EmptyState, Pagination, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { forumApi } from '@/api'
import { useAuthStore } from '@/store/authStore'
import type { Thread, Post } from '@/api/forum'

export default function ThreadDetailPage() {
  const params = useParams<{ id?: string; threadId?: string }>()
  const id = params.threadId || params.id
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [thread, setThread] = useState<Thread | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [replyContent, setReplyContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [editingPost, setEditingPost] = useState<Post | null>(null)
  const [editContent, setEditContent] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'thread' | 'post', id: number } | null>(null)

  useEffect(() => {
    if (id) {
      fetchThreadData()
    }
  }, [id, currentPage])

  const fetchThreadData = async () => {
    setLoading(true)
    try {
      const [threadRes, postsRes] = await Promise.allSettled([
        forumApi.getThread(Number(id)),
        forumApi.getPosts(Number(id), currentPage),
      ])

      if (threadRes.status === 'fulfilled') {
        setThread(threadRes.value)
      }

      if (postsRes.status === 'fulfilled') {
        setPosts(postsRes.value.results)
        setTotalPages(Math.ceil(postsRes.value.count / 20))
        setTotalItems(postsRes.value.count)
      }
    } catch (error) {
      console.error('Error fetching thread data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!replyContent.trim() || !thread) return

    setSubmitting(true)
    try {
      await forumApi.createPost(thread.id, {
        content: replyContent,
      })
      setReplyContent('')
      fetchThreadData()
    } catch (error) {
      console.error('Error submitting reply:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditPost = async () => {
    if (!editingPost || !editContent.trim()) return

    try {
      await forumApi.updatePost(editingPost.id, editContent)
      setEditingPost(null)
      setEditContent('')
      fetchThreadData()
    } catch (error) {
      console.error('Error editing post:', error)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return

    try {
      if (deleteTarget.type === 'thread') {
        await forumApi.deleteThread(deleteTarget.id)
        navigate('/forum')
      } else {
        await forumApi.deletePost(deleteTarget.id)
        fetchThreadData()
      }
    } catch (error) {
      console.error('Error deleting:', error)
    } finally {
      setShowDeleteModal(false)
      setDeleteTarget(null)
    }
  }

  const handleMarkSolution = async (postId: number) => {
    try {
      await forumApi.markAsSolution(postId)
      fetchThreadData()
    } catch (error) {
      console.error('Error marking solution:', error)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isAuthor = (authorId: number) => user?.id === authorId
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento discussione..." />
  }

  if (!thread) {
    return (
      <EmptyState
        icon={<ChatBubbleLeftRightIcon className="h-12 w-12" />}
        title="Discussione non trovata"
        description="La discussione richiesta non esiste"
        action={{
          label: 'Torna al Forum',
          onClick: () => navigate('/forum'),
        }}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/forum" className="hover:text-primary-600">
          Forum
        </Link>
        <ChevronRightIcon className="h-4 w-4" />
        <Link to={`/forum/category/${thread.forum.slug}`} className="hover:text-primary-600">
          {thread.forum.name}
        </Link>
        <ChevronRightIcon className="h-4 w-4" />
        <span className="text-gray-900 truncate max-w-xs">{thread.title}</span>
      </nav>

      {/* Thread Header */}
      <Card>
        <div className="flex items-start gap-4">
          <Avatar
            size="lg"
            name={thread.author.display_name}
            src={thread.author.avatar_url || undefined}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {thread.is_pinned && (
                <Badge size="sm" variant="warning">Fissato</Badge>
              )}
              {thread.is_locked && (
                <Badge size="sm" variant="danger">
                  <LockClosedIcon className="h-3 w-3 mr-1" />
                  Chiuso
                </Badge>
              )}
            </div>
            <h1 className="text-xl font-bold text-gray-900 mt-2">{thread.title}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="font-medium text-gray-700">{thread.author.display_name}</span>
              <Badge size="sm">{thread.author.role}</Badge>
              <span className="flex items-center">
                <ClockIcon className="h-4 w-4 mr-1" />
                {formatDate(thread.created_at)}
              </span>
              <span className="flex items-center">
                <EyeIcon className="h-4 w-4 mr-1" />
                {thread.views_count} visualizzazioni
              </span>
              <span className="flex items-center">
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                {thread.posts_count} risposte
              </span>
            </div>
            <div
              className="mt-4 prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: thread.content }}
            />

            {/* Thread Actions */}
            {(isAuthor(thread.author.id) || isAdmin) && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/forum/edit/${thread.id}`)}
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Modifica
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => {
                    setDeleteTarget({ type: 'thread', id: thread.id })
                    setShowDeleteModal(true)
                  }}
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Elimina
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Posts */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Risposte ({totalItems})
        </h2>

        {posts.length > 0 ? (
          <>
            {posts.map((post) => (
              <Card key={post.id} className={post.is_solution ? 'border-2 border-green-500' : ''}>
                {post.is_solution && (
                  <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100 text-green-600">
                    <CheckCircleIcon className="h-5 w-5" />
                    <span className="font-medium">Soluzione accettata</span>
                  </div>
                )}
                <div className="flex items-start gap-4">
                  <Avatar
                    size="md"
                    name={post.author.display_name}
                    src={post.author.avatar_url || undefined}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{post.author.display_name}</span>
                        <Badge size="sm">{post.author.role}</Badge>
                        <span className="text-xs text-gray-500">
                          {formatDate(post.created_at)}
                        </span>
                        {post.is_edited && (
                          <span className="text-xs text-gray-400">(modificato)</span>
                        )}
                      </div>
                    </div>

                    {editingPost?.id === post.id ? (
                      <div className="mt-3">
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          rows={4}
                        />
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" onClick={handleEditPost}>
                            Salva
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingPost(null)
                              setEditContent('')
                            }}
                          >
                            Annulla
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="mt-3 prose prose-sm max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                      />
                    )}

                    {/* Post Actions */}
                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                      {thread.type === 'question' && !post.is_solution && isAuthor(thread.author.id) && !isAuthor(post.author.id) && (
                        <button
                          onClick={() => handleMarkSolution(post.id)}
                          className="flex items-center gap-1 text-sm text-gray-500 hover:text-green-600"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          Segna come soluzione
                        </button>
                      )}

                      <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600">
                        <FlagIcon className="h-4 w-4" />
                        Segnala
                      </button>

                      {(isAuthor(post.author.id) || isAdmin) && (
                        <>
                          <button
                            onClick={() => {
                              setEditingPost(post)
                              setEditContent(post.content)
                            }}
                            className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary-600"
                          >
                            <PencilIcon className="h-4 w-4" />
                            Modifica
                          </button>
                          <button
                            onClick={() => {
                              setDeleteTarget({ type: 'post', id: post.id })
                              setShowDeleteModal(true)
                            }}
                            className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                            Elimina
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
            />
          </>
        ) : (
          <Card>
            <p className="text-center text-gray-500 py-8">
              Nessuna risposta ancora. Sii il primo a rispondere!
            </p>
          </Card>
        )}
      </div>

      {/* Reply Form */}
      {!thread.is_locked ? (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Rispondi alla discussione</h3>
          <form onSubmit={handleSubmitReply}>
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Scrivi la tua risposta..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={5}
              required
            />
            <div className="flex justify-end mt-4">
              <Button type="submit" disabled={submitting || !replyContent.trim()}>
                {submitting ? 'Invio in corso...' : 'Pubblica risposta'}
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Card>
          <div className="flex items-center justify-center gap-2 text-gray-500 py-4">
            <LockClosedIcon className="h-5 w-5" />
            <span>Questa discussione è chiusa e non accetta nuove risposte.</span>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setDeleteTarget(null)
        }}
        title="Conferma eliminazione"
      >
        <ModalBody>
          <p>
            Sei sicuro di voler eliminare {deleteTarget?.type === 'thread' ? 'questa discussione' : 'questo messaggio'}?
            Questa azione non può essere annullata.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setShowDeleteModal(false)
              setDeleteTarget(null)
            }}
          >
            Annulla
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
          >
            Elimina
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
